import React, { useState, useEffect, useCallback } from 'react';
import { adminReviews } from '../../utils/api';
import toast from 'react-hot-toast';

const Stars = ({ rating }) => {
  const r = parseInt(rating) || 0;
  return (
    <span style={{ color: '#D4943A', letterSpacing: 1, fontSize: '0.95rem' }}>
      {'★'.repeat(r)}{'☆'.repeat(Math.max(0, 5 - r))}
    </span>
  );
};

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminReviews.getPending()
      .then(setReviews)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (r) => {
    try {
      await adminReviews.approve(r.id);
      toast.success('Review approved');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete this review${r.title ? ` (${r.title})` : ''}? This cannot be undone.`)) return;
    try {
      await adminReviews.delete(r.id);
      toast.success('Review deleted');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div>
      <div className="admin-page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-serif)', fontSize:'1.5rem' }}>
            Pending Reviews <span style={{ fontSize:'1rem', color:'var(--text-muted)', fontFamily:'var(--font-sans)' }}>({reviews.length})</span>
          </h2>
          <p style={{ color:'var(--text-muted)', marginTop:4, fontSize:'0.88rem' }}>
            Approve to publish on the storefront, or delete to remove permanently.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="admin-card"><div style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></div></div>
      ) : reviews.length === 0 ? (
        <div className="admin-card" style={{ textAlign:'center', padding:60 }}>
          <p style={{ fontSize:'1rem' }}>🎉 No pending reviews.</p>
          <p style={{ color:'var(--text-muted)', marginTop:6, fontSize:'0.88rem' }}>You're all caught up.</p>
        </div>
      ) : (
        <div className="reviews-grid">
          {reviews.map(r => (
            <div key={r.id} className="review-card">
              <div className="review-card__top">
                <div>
                  <p className="review-product">{r.product_name || `Product #${r.product_id}`}</p>
                  <Stars rating={r.rating} />
                </div>
                {r.is_verified_purchase ? <span className="badge badge-olive">Verified</span> : null}
              </div>

              {r.title && <h4 className="review-title">{r.title}</h4>}
              {r.body && <p className="review-body">{r.body}</p>}

              <div className="review-meta">
                <span>👤 {r.guest_name || r.user_email || 'Anonymous'}</span>
                <span>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</span>
              </div>

              <div className="review-actions">
                <button className="btn btn-primary" onClick={() => handleApprove(r)}>✓ Approve</button>
                <button className="action-btn action-btn--danger" onClick={() => handleDelete(r)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .admin-card { background: white; border-radius: var(--radius-lg); padding: 20px; box-shadow: var(--shadow-sm); }
        .reviews-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 16px; }
        .review-card { background: white; border-radius: var(--radius-lg); padding: 20px; box-shadow: var(--shadow-sm); display: flex; flex-direction: column; gap: 12px; }
        .review-card__top { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
        .review-product { font-weight: 600; font-size: 0.9rem; margin-bottom: 4px; }
        .review-title { font-family: var(--font-serif); font-size: 1rem; }
        .review-body { color: var(--text-muted); font-size: 0.88rem; line-height: 1.5; }
        .review-meta { display: flex; justify-content: space-between; font-size: 0.78rem; color: var(--text-muted); padding-top: 10px; border-top: 1px solid #f0f0f0; }
        .review-actions { display: flex; gap: 8px; margin-top: 4px; }
        .review-actions .btn-primary { padding: 8px 14px; font-size: 0.82rem; }
        .action-btn { padding: 8px 14px; background: var(--cream); border-radius: 6px; font-size: 0.82rem; font-weight: 600; font-family: var(--font-sans); transition: all 0.2s; }
        .action-btn--danger:hover { background: #e53e3e; color: white; }
      `}</style>
    </div>
  );
};

export default AdminReviews;
